/**
 * PromotionRing — the Explorer → Contributor ring animation (variant E,
 * "Heartbeat", Skip-approved 2026-07-07 from the mockup session).
 *
 * Sequence: two heartbeat pulses of the avatar, each emitting a soft
 * ripple (anticipation) → the trust ring draws in Enabler blue → a flash
 * ring + third pulse + final ripple on landing. The ring then STAYS blue —
 * the persistent ring is the point: wearable trust that will carry to the
 * header/comments/team cards once the platform-wide rung-ring system ships
 * (tracked separately; this modal moment stands alone until then).
 *
 * Motion discipline: solid strokes only (no glow/gradient per CLAUDE.md),
 * ~2.2s total, and `prefers-reduced-motion` collapses the whole sequence
 * to the final state (instant blue ring, no pulses).
 *
 * Color: var(--pillar-enablers) — the Enabler pillar blue. Deliberate
 * (Skip 2026-07-07): the Contributor ring celebrates joining the layer of
 * people who apply solutions. Users are never LABELED "Enablers"
 * (POLICY_ENABLER_VOCABULARY); the label stays Contributor.
 */

import React, { useEffect, useRef } from 'react';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './PromotionRing.module.css';

const RING_R = 48;
const RING_C = 2 * Math.PI * RING_R; // ≈ 301.6

interface PromotionRingProps {
  /** Profile photo URL (raw; resolved via mediaSrc). Falls back to initials. */
  avatarUrl?: string | null;
  /** Display name — used for the initials fallback. */
  displayName?: string;
  /** Rendered pixel size of the ring stage. viewBox is fixed at 180, so a
   *  larger size scales the whole avatar+ring up uniformly. Default 180.
   *  The Contributor reveal passes a larger value to make the avatar+ring
   *  the emotional centerpiece (updated design direction 2026-07-08). */
  size?: number;
}

function initialsOf(name?: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

export default function PromotionRing({ avatarUrl, displayName, size = 180 }: PromotionRingProps) {
  const ringRef = useRef<SVGCircleElement | null>(null);
  const flashRef = useRef<SVGCircleElement | null>(null);
  const avatarRef = useRef<SVGGElement | null>(null);
  const ripplesRef = useRef<SVGGElement | null>(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ring = ringRef.current;
    const flash = flashRef.current;
    const avatar = avatarRef.current;
    const ripples = ripplesRef.current;
    if (!ring || !flash || !avatar || !ripples) return;

    if (reduced) {
      // Final state, no motion.
      ring.style.transition = 'none';
      ring.setAttribute('stroke-dashoffset', '0');
      return;
    }

    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };

    const ripple = (delay: number, maxR: number, width: number, opacity: number) => {
      later(() => {
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        c.setAttribute('cx', '90');
        c.setAttribute('cy', '90');
        c.setAttribute('r', '50');
        c.setAttribute('fill', 'none');
        c.setAttribute('stroke', 'var(--pillar-enablers, #3b82f6)');
        c.setAttribute('stroke-width', String(width));
        c.setAttribute('opacity', String(opacity));
        ripples.appendChild(c);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            c.style.transition =
              'r 800ms cubic-bezier(0.2, 0.6, 0.4, 1), opacity 800ms ease-out, stroke-width 800ms ease-out';
            c.setAttribute('r', String(maxR));
            c.setAttribute('opacity', '0');
            c.setAttribute('stroke-width', '1');
            timers.push(window.setTimeout(() => c.remove(), 900));
          }),
        );
      }, delay);
    };

    const pulse = (delay: number, scale: number, dur: number) => {
      later(() => {
        avatar.style.transition = `transform ${Math.round(dur / 2)}ms cubic-bezier(0.3, 0, 0.4, 1)`;
        avatar.style.transform = `scale(${scale})`;
        timers.push(
          window.setTimeout(() => {
            avatar.style.transition = `transform ${Math.round(dur / 2)}ms cubic-bezier(0.2, 0.8, 0.4, 1)`;
            avatar.style.transform = 'scale(1)';
          }, dur / 2),
        );
      }, delay);
    };

    // ── Variant E: heartbeat ×2 → ring draw → flash + landing beat ──
    pulse(100, 1.06, 360);
    ripple(160, 76, 3, 0.55);
    pulse(560, 1.08, 360);
    ripple(620, 80, 3, 0.55);
    later(() => {
      ring.style.transition = 'stroke-dashoffset 550ms cubic-bezier(0.4, 0, 0.2, 1)';
      ring.setAttribute('stroke-dashoffset', '0');
    }, 950);
    later(() => {
      flash.style.transition = 'none';
      flash.setAttribute('opacity', '0.9');
      flash.setAttribute('r', String(RING_R));
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          flash.style.transition = 'opacity 450ms ease-out, r 450ms ease-out';
          flash.setAttribute('opacity', '0');
          flash.setAttribute('r', '56');
        }),
      );
    }, 1500);
    pulse(1500, 1.05, 400);
    ripple(1520, 84, 3.5, 0.6);

    return () => timers.forEach(window.clearTimeout);
  }, []);

  const resolvedUrl = avatarUrl ? mediaSrc(avatarUrl) : null;

  return (
    <div className={styles.stage} aria-hidden="true">
      <svg width={size} height={size} viewBox="0 0 180 180">
        <g ref={ripplesRef} />
        <circle
          cx="90"
          cy="90"
          r={RING_R}
          fill="none"
          className={styles.baseRing}
          strokeWidth="6"
        />
        <circle
          ref={ringRef}
          cx="90"
          cy="90"
          r={RING_R}
          fill="none"
          stroke="var(--pillar-enablers, #3b82f6)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C}
          transform="rotate(-90 90 90)"
          className={styles.ring}
        />
        <circle
          ref={flashRef}
          cx="90"
          cy="90"
          r={RING_R}
          fill="none"
          stroke="var(--pillar-enablers, #3b82f6)"
          strokeWidth="7"
          opacity="0"
          className={styles.ring}
        />
        <g ref={avatarRef} className={styles.avatarGroup}>
          {resolvedUrl ? (
            <>
              <clipPath id="promotion-ring-avatar-clip">
                <circle cx="90" cy="90" r="41" />
              </clipPath>
              <image
                href={resolvedUrl}
                x="49"
                y="49"
                width="82"
                height="82"
                preserveAspectRatio="xMidYMid slice"
                clipPath="url(#promotion-ring-avatar-clip)"
              />
            </>
          ) : (
            <>
              <circle cx="90" cy="90" r="41" className={styles.avatarFill} />
              <text x="90" y="98" textAnchor="middle" className={styles.avatarInitials}>
                {initialsOf(displayName)}
              </text>
            </>
          )}
        </g>
      </svg>
    </div>
  );
}
